import { useState, useEffect, useRef, type ReactNode } from "react";
import { useGT } from "gt-react";
import { connectSharedConfig, initialize, initTheme, refreshRuntimeStatus, useAppStore, useObserverStore, useRuntimeStore, useServersStore, useQueriesStore, useSharedSyncStore, selectServer } from "./store";
import { Sidebar } from "./components/Sidebar";
import { QueryEditor } from "./components/QueryEditor";
import { ResultPane } from "./components/ResultPane";
import { SplitPane } from "./components/SplitPane";
import { SecondaryQueryPane } from "./components/SecondaryQueryPane";
import { ObserverPanel } from "./components/ObserverPanel";
import { KeyboardShortcutsHelp } from "./components/KeyboardShortcutsHelp";
import { QuickOpen } from "./components/QuickOpen";
import { ServerSelector } from "./components/ServerSelector";
import { ImpressumDialog, DatenschutzDialog } from "./components/LegalDialog";
import { Toasts } from "./components/Toasts";
import { ThemeToggle } from "./components/ThemeToggle";
import { AppMenu } from "./components/AppMenu";
import { VaultManagerDialog } from "./components/VaultManagerDialog";
import { LocalRuntimeDialog } from "./components/LocalRuntimeDialog";
import { LocalAppsDialog } from "./components/LocalAppsDialog";
import { ImportPreviewDialog } from "./components/ImportPreviewDialog";
import { CURRENT_RELEASE, fetchLatestReleaseManifest, type ReleaseManifest } from "./release";
import { AppWindow } from "lucide-react";
import { SchemaBrowserOverlay } from "./components/SchemaBrowser";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useSidebarResize } from "./hooks/useSidebarResize";
import { useSharedConfigSync } from "./hooks/useSharedConfigSync";
import { useUserDataSync } from "./hooks/useUserDataSync";
import { toast } from "./components/Toasts";
import { AuthCallback } from "./components/AuthCallback";
import { AccountMenu } from "./components/AccountMenu";
import { SyncIndicator } from "./components/SyncIndicator";
import { UpgradeButton } from "./components/UpgradeButton";
import { useAuthStore } from "./authStore";
import { getRuntimeWebSocketUrl, startRuntimeLogin } from "./runtimeClient";
import { createRuntimeObserverClient } from "./runtimeObserverClient";
import { acceptSharedConfigInvite } from "./sharedConfigClient";


const isElectron = navigator.userAgent.includes("Electron");
const isDesktop = window.location.protocol === "file:";
const RELEASE_POLL_INTERVAL_MS = 5 * 60 * 1000;
const RUNTIME_POLL_INTERVAL_MS = 15_000;

function getObserverClientId(surface: "web" | "desktop"): string {
  const storageKey = `spuerhund:observer-client-id:${surface}`;

  try {
    const existing = sessionStorage.getItem(storageKey)?.trim();
    if (existing) return existing;

    const created = `observer-${surface}-${crypto.randomUUID()}`;
    sessionStorage.setItem(storageKey, created);
    return created;
  } catch {
    return `observer-${surface}-${crypto.randomUUID()}`;
  }
}

// Check for standalone schema view mode
function getStandaloneParams(): { view: string; serverId: string } | null {
  const params = new URLSearchParams(window.location.search);
  const view = params.get("view");
  const serverId = params.get("server");
  if (view === "schema" && serverId) {
    return { view, serverId };
  }
  return null;
}

export function App() {
  // Handle /auth/callback route before anything else
  if (window.location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }

  const t = useGT();
  const initCalled = useRef(false);
  const handledInviteConfigId = useRef<string | null>(null);
  const observerId = useRef<string>(getObserverClientId(isDesktop ? "desktop" : "web"));
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const showQuickOpen = useAppStore((s) => s.showQuickOpen);
  const showVaultManager = useAppStore((s) => s.showVaultManager);
  const showRuntimeDialog = useAppStore((s) => s.showRuntimeDialog);
  const showLocalAppsDialog = useAppStore((s) => s.showLocalAppsDialog);
  const importPreview = useAppStore((s) => s.importPreview);
  const setShowLocalAppsDialog = useAppStore((s) => s.setShowLocalAppsDialog);
  const activePaneId = useAppStore((s) => s.activePaneId);
  const setActivePaneId = useAppStore((s) => s.setActivePaneId);
  const secondaryPanels = useAppStore((s) => s.secondaryPanels);
  const openSecondaryPane = useAppStore((s) => s.openSecondaryPane);
  const closeSecondaryPane = useAppStore((s) => s.closeSecondaryPane);
  const sidebar = useSidebarResize();
  const activeServerId = useServersStore((s) => s.activeServerId);
  const servers = useServersStore((s) => s.servers);
  const activeServer = servers.find((s) => s.id === activeServerId);
  const isProd = !!activeServer?.isProduction;
  const queries = useQueriesStore((s) => s.queries);
  const activeQueryId = useQueriesStore((s) => s.activeQueryId);
  const runtimeReachable = useRuntimeStore((s) => s.reachable);
  const runtimePaired = useRuntimeStore((s) => s.paired);
  const [availableRelease, setAvailableRelease] = useState<ReleaseManifest | null>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const runtimeLinkLabel = runtimeReachable
    ? runtimePaired ? null : t("Not Paired")
    : null;
  const runtimeLinkTitle = runtimeReachable
    ? runtimePaired
      ? t("Local Runtime paired. Requests run through the local runtime.")
      : t("Local Runtime found, but this browser is not paired yet.")
    : t("Local Runtime not found. Click for setup help.");
  const runtimeLinkClassName = runtimeReachable
    ? runtimePaired
      ? "text-success hover:text-success/80"
      : "text-warning hover:text-warning/80"
    : "text-zinc-600 hover:text-zinc-400";

  const standaloneParams = getStandaloneParams();

  const focusedQueryId = activePaneId === "primary"
    ? activeQueryId
    : secondaryPanels.find((pane) => pane.id === activePaneId)?.queryId ?? activeQueryId;
  const hasMultiplePanels = secondaryPanels.length > 0;

  const panelChrome = (isActive: boolean) => ({
    borderColor: hasMultiplePanels && isActive ? "var(--color-brand)" : "var(--color-border)",
    boxShadow: hasMultiplePanels && isActive ? "inset 0 0 0 1px var(--color-brand)" : undefined,
  });

  const renderPanelStack = (nodes: ReactNode[], depth = 0): ReactNode => {
    if (nodes.length === 1) return nodes[0]!;
    return (
      <SplitPane direction="horizontal" storageKey={`spuerhund:query-panels:${depth}:${nodes.length}`}>
        {[nodes[0]!, renderPanelStack(nodes.slice(1), depth + 1)]}
      </SplitPane>
    );
  };

  useKeyboardShortcuts();
  useSharedConfigSync();
  useUserDataSync();

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      useSharedSyncStore.getState().pruneRecentRemoteUpdates();
    }, 2_000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!initCalled.current) {
      initCalled.current = true;
      initTheme();
      useAuthStore.getState().restore();
      void (async () => {
        try {
          const { startupWarnings } = await initialize();

          if (standaloneParams) {
            await selectServer(standaloneParams.serverId);
          }

          const sharedUrl = new URL(window.location.href);
          if (sharedUrl.searchParams.has("shared") && sharedUrl.hash.includes("key=")) {
            try {
              await connectSharedConfig(window.location.href);
              sharedUrl.searchParams.delete("shared");
              sharedUrl.hash = "";
              window.history.replaceState({}, "", sharedUrl.toString());
            } catch {
              // Ignore auto-connect failures here; user can retry from the dialog.
            }
          }

          // In desktop mode, wait for runtime to be reachable and fetch auth status
          if (isDesktop) {
            for (let i = 0; i < 10; i++) {
              await refreshRuntimeStatus();
              if (useRuntimeStore.getState().reachable) break;
              await new Promise((r) => setTimeout(r, 500));
            }
          }

          setReady(true);
          if (startupWarnings.length > 0) {
            window.setTimeout(() => {
              for (const warning of startupWarnings) {
                toast(warning, "error");
              }
            }, 0);
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          console.error("[app] initialization failed", error);
          setInitError(message);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (!ready || !isAuthenticated) return;

    const inviteUrl = new URL(window.location.href);
    const inviteConfigId = inviteUrl.searchParams.get("invite");
    if (!inviteConfigId || handledInviteConfigId.current === inviteConfigId) return;

    handledInviteConfigId.current = inviteConfigId;

    void (async () => {
      try {
        let joined = false;
        try {
          await acceptSharedConfigInvite(inviteConfigId);
          joined = true;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (!message.includes("No pending invite found")) {
            throw error;
          }
        }

        await connectSharedConfig(inviteConfigId, { managedByWorkspace: true });
        inviteUrl.searchParams.delete("invite");
        window.history.replaceState({}, "", inviteUrl.toString());
        toast(joined ? "Shared server joined" : "Shared server opened", "success");
      } catch (error) {
        handledInviteConfigId.current = null;
        toast(error instanceof Error ? error.message : "Failed to open shared server invite", "error");
      }
    })();
  }, [ready, isAuthenticated]);

  useEffect(() => {
    if (!ready) return;

    let cancelled = false;

    const checkForUpdate = async () => {
      try {
        const latest = await fetchLatestReleaseManifest();
        if (cancelled) return;
        if (latest.id !== CURRENT_RELEASE.id) {
          setAvailableRelease(latest);
        } else {
          setAvailableRelease(null);
        }
      } catch {
        // Ignore transient fetch issues.
      }
    };

    void checkForUpdate();
    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, RELEASE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [ready]);

  useEffect(() => {
    if (!ready || !runtimeReachable || !runtimePaired) {
      useObserverStore.getState().setConnected(false);
      useObserverStore.getState().setApprovalHandlers(null);
      return;
    }

    const observerClient = createRuntimeObserverClient({
      runtimeWsUrl: getRuntimeWebSocketUrl(),
      observerId: observerId.current,
      observerName: isDesktop ? "Spuerhund Desktop" : "Spuerhund Web",
      capabilities: ["observe", "approve"],
      onEvent: (event) => useObserverStore.getState().applyRuntimeObserverEvent(event),
      onConnectionChange: (connected) => useObserverStore.getState().setConnected(connected),
    });

    useObserverStore.getState().setApprovalHandlers({
      approveExecution: (executionId) => observerClient.approve(executionId),
      rejectExecution: (executionId, reason) => observerClient.reject(executionId, reason),
    });
    observerClient.connect();

    return () => {
      useObserverStore.getState().setApprovalHandlers(null);
      observerClient.disconnect();
    };
  }, [ready, runtimeReachable, runtimePaired]);

  useEffect(() => {
    if (!ready) return;

    void refreshRuntimeStatus();
    const intervalId = window.setInterval(() => {
      void refreshRuntimeStatus();
    }, RUNTIME_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [ready]);

  if (initError) {
    return (
      <div className="flex h-full items-center justify-center bg-surface px-6">
          <div className="w-full max-w-xl rounded-2xl border border-border bg-surface-raised p-6 shadow-lg">
          <div className="text-lg font-semibold text-zinc-100">{t("Spürhund could not start locally")}</div>
          <p className="mt-2 text-sm leading-6 text-zinc-400">{initError}</p>
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-amber-400"
            >
              {t("Reload")}
            </button>
            <button
              onClick={() => window.location.href = window.location.href}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-surface"
            >
              {t("Retry")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!ready) {
    return <div className="flex h-full bg-surface" />;
  }

  // Desktop login gate
  if (isDesktop && !isAuthenticated) {
    return <DesktopLoginGate />;
  }

  // Standalone schema browser mode
  if (standaloneParams) {
    return (
      <div className="flex h-full flex-col bg-surface">
        <SchemaBrowserOverlay standalone serverId={standaloneParams.serverId} />
        <Toasts />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-surface">
      <header className={`flex h-10 shrink-0 items-center border-b border-border px-4${isElectron ? " electron-titlebar-pad" : ""}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <img src="./hund.svg" alt="" className="h-5 w-5" />
            <span className="text-sm font-semibold tracking-tight text-zinc-100">
              Spürhund
            </span>
          </div>
          <ServerSelector />
          {isProd && (
            <span className="text-[11px] font-bold tracking-widest text-danger">PRODUCTION</span>
          )}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          {availableRelease && (
            <button
              onClick={() => window.location.reload()}
              className="rounded border border-warning/40 bg-warning-bg px-2.5 py-1 text-[11px] font-medium text-warning hover:bg-warning-bg/80"
              title={t("Reload to update to {title}", { title: availableRelease.title })}
            >
              {t("Update available")}
            </button>
          )}
          {!isDesktop && (
            <button
              onClick={() => setShowLocalAppsDialog(true)}
              className={`flex items-center gap-1.5 rounded px-2 py-1 ${runtimeLinkClassName}`}
              title={runtimeLinkTitle ?? t("Desktop app, local runtime, CLI")}
              aria-label={t("Local Apps")}
            >
              <AppWindow size={13} />
              <span className="text-[11px] font-medium">
                {runtimeLinkLabel ?? t("Local Apps")}
              </span>
            </button>
          )}
          <SyncIndicator />
          <div className="mx-0.5 h-4 w-px bg-border" />
          <ThemeToggle />
          {!isDesktop && <UpgradeButton />}
          <AccountMenu />
          <div className="mx-0.5 h-4 w-px bg-border" />
          <AppMenu />
        </div>
      </header>
      <div
        className="flex flex-1 overflow-hidden"
        style={{ userSelect: sidebar.dragging ? "none" : undefined }}
      >
        <Sidebar style={{ width: sidebar.width }} />
        {/* Sidebar resize handle */}
        <div
          onPointerDown={sidebar.onPointerDown}
          onPointerMove={sidebar.onPointerMove}
          onPointerUp={sidebar.onPointerUp}
          style={{
            flexShrink: 0,
            width: 4,
            cursor: "col-resize",
            background: sidebar.dragging ? "var(--color-divider-active)" : "var(--color-divider)",
            transition: sidebar.dragging ? "none" : "background 150ms",
            touchAction: "none",
          }}
          onMouseEnter={(e) => {
            if (!sidebar.dragging) e.currentTarget.style.background = "var(--color-divider-hover)";
          }}
          onMouseLeave={(e) => {
            if (!sidebar.dragging) e.currentTarget.style.background = "var(--color-divider)";
          }}
        />
        {/* All Panes — horizontally resizable */}
        <div className="flex flex-1 overflow-hidden">
          {renderPanelStack([
            <div
              key="primary"
              className="flex flex-1 flex-col overflow-hidden border"
              style={{ minWidth: 0, ...panelChrome(activePaneId === "primary") }}
              onMouseDownCapture={() => setActivePaneId("primary")}
            >
              <SplitPane direction="vertical">
                <QueryEditor
                  headerAction={
                    <button
                      onClick={() => {
                        if (focusedQueryId) openSecondaryPane(focusedQueryId);
                      }}
                      disabled={!focusedQueryId}
                      className="flex shrink-0 items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
                      title={t("Open another panel")}
                    >
                      <span className="text-sm leading-none">+</span>
                      <span>{t("Panel")}</span>
                    </button>
                  }
                />
                <ResultPane />
              </SplitPane>
            </div>,
            ...secondaryPanels.map((pane) => (
              <div
                key={pane.id}
                className="flex flex-1 flex-col overflow-hidden border"
                style={{ minWidth: 0, ...panelChrome(activePaneId === pane.id) }}
                onMouseDownCapture={() => setActivePaneId(pane.id)}
              >
                <SecondaryQueryPane
                  panelId={pane.id}
                  queryId={pane.queryId}
                  onClose={() => closeSecondaryPane(pane.id)}
                />
              </div>
            )),
          ])}
        </div>
        <ObserverPanel />
      </div>
      {!isElectron && (
        <>
          <footer className="flex h-6 shrink-0 items-center justify-between border-t border-border bg-surface px-4 text-[10px] text-zinc-600">
            <div className="flex items-center gap-1">
              <span>{t("A project by")}</span>
              <a href="https://s16e.de" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-300">Sebastian Breitzke · s16e.de</a>
              <span className="text-zinc-700">·</span>
              <span>{t("AI, Product, Architecture or Code?")}</span>
              <a href="https://s16e.de/consulting/" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-300">Hire me</a>
              <span className="text-zinc-700">·</span>
              <span>{t("Want to support me?")}</span>
              <a href="https://github.com/sponsors/sebastian-breitzke" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-300">Sponsor me on GitHub</a>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowImpressum(true)} className="text-zinc-600 hover:text-zinc-300">
                {t("Legal Notice")}
              </button>
              <button onClick={() => setShowDatenschutz(true)} className="text-zinc-600 hover:text-zinc-300">
                {t("Privacy")}
              </button>
            </div>
          </footer>
        </>
      )}
      <KeyboardShortcutsHelp />
      {showQuickOpen && <QuickOpen />}
      <SchemaBrowserOverlay />
      {showVaultManager && <VaultManagerDialog />}
      {showLocalAppsDialog && <LocalAppsDialog />}
      {showRuntimeDialog && <LocalRuntimeDialog />}
      {importPreview && <ImportPreviewDialog />}
      <ImpressumDialog open={showImpressum} onClose={() => setShowImpressum(false)} />
      <DatenschutzDialog open={showDatenschutz} onClose={() => setShowDatenschutz(false)} />
      <Toasts />
    </div>
  );
}

function DesktopLoginGate() {
  const [loading, setLoading] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Poll runtime for auth status
  useEffect(() => {
    if (isAuthenticated) return;
    const interval = setInterval(() => void refreshRuntimeStatus(), 2000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const { url } = await startRuntimeLogin();
      window.open(url, "_blank");
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col items-center justify-center bg-surface">
      <div className="flex flex-col items-center gap-6 text-center">
        <img src="./hund.svg" alt="" className="h-16 w-16" />
        <h1 className="text-2xl font-semibold text-zinc-100">Spürhund</h1>
        <p className="max-w-sm text-sm text-zinc-400">
          Sign in to get started. Your browser will open for authentication.
        </p>
        <button
          onClick={() => void handleLogin()}
          disabled={loading}
          className="rounded bg-brand px-6 py-2 text-sm font-semibold text-zinc-900 hover:bg-brand-light disabled:opacity-50"
        >
          {loading ? "Waiting for browser…" : "Sign In"}
        </button>
      </div>
      <Toasts />
    </div>
  );
}
